import React, { useEffect, useRef, useState } from 'react';
import { Tooltip, TooltipProps } from '@mui/material';
import { unstable_useId as useId } from '@mui/material/utils';
import { MOBILE_SX, useIsMobileApp } from '../../mobile/mobileApp';

/** How long a tapped tip stays open on the phone, unless tapped away first. */
const PHONE_TIP_MS = 8000;

/**
 * On the phone the tap's click opens the tip, a second tap or a tap elsewhere
 * closes it; a tap inside the tip (its tabs, say) keeps it open. MUI's own
 * touch opening starts a timer on touchstart that the touchend cancels when the
 * page is busy, so a normal quick tap often did nothing.
 */
const PhoneTapTooltip: React.FC<TooltipProps> = ({ children, id: idProp, ...props }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLElement | null>(null);
  // MUI puts the tooltip's id on its popper, so a tap inside the tip can be told apart.
  const id = useId(idProp);
  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => setOpen(false), PHONE_TIP_MS);
    // Capture phase: a tap on another tip's icon stops its click from bubbling,
    // and this one still has to close.
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (anchorRef.current?.contains(target)) return;
      if (id && document.getElementById(id)?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('click', onDocumentClick, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('click', onDocumentClick, true);
    };
  }, [open, id]);
  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    anchorRef.current = event.currentTarget;
    children.props.onClick?.(event);
    setOpen((was) => !was);
  };
  return (
    <Tooltip {...props} id={id} open={open} disableHoverListener disableFocusListener disableTouchListener>
      {React.cloneElement(children, { onClick })}
    </Tooltip>
  );
};

/**
 * A bare MUI Tooltip for an info icon: on the phone a tap opens it and it
 * stays long enough to read. Elsewhere it is a plain Tooltip.
 */
export const TapTooltip: React.FC<TooltipProps> = (props) => {
  const isMobileApp = useIsMobileApp();
  return isMobileApp ? <PhoneTapTooltip {...props} /> : <Tooltip {...props} />;
};

/**
 * The house tooltip — the treatment the "How To Improve" tooltip introduced on
 * the Development Tier modal: a soft dark card on a dim hairline with a deep
 * shadow, no arrow, Inter at 11.5.
 *
 * Use this instead of a bare MUI `Tooltip` so every hover on a surface reads
 * the same. Anything a plain Tooltip accepts (placement, enterDelay, open,
 * componentsProps for a per-use override) passes straight through.
 *
 * `tapOnPhone` is for info icons: see TapTooltip. Long text scrolls inside it there.
 */
const DosenTooltip: React.FC<TooltipProps & { tapOnPhone?: boolean }> = ({
  children,
  componentsProps,
  tapOnPhone = false,
  ...rest
}) => {
  const Base = tapOnPhone ? TapTooltip : Tooltip;
  return (
    <Base
      {...rest}
      componentsProps={{
        ...componentsProps,
        tooltip: {
          ...componentsProps?.tooltip,
          sx: {
            bgcolor: '#232323',
            border: '1px solid #2A2A2A',
            borderRadius: '8px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
            p: '10px 12px',
            maxWidth: 320,
            fontFamily: 'Inter, sans-serif',
            fontSize: 11.5,
            fontWeight: 400,
            lineHeight: 1.45,
            color: '#B6B6B6',
            ...(tapOnPhone
              ? { [MOBILE_SX]: { maxWidth: 'calc(100vw - 40px)', maxHeight: '70vh', overflowY: 'auto' } }
              : null),
            // Callers that need something bespoke can still override.
            ...(componentsProps?.tooltip as { sx?: object } | undefined)?.sx
          }
        }
      }}
    >
      {children}
    </Base>
  );
};

export default DosenTooltip;
